// Legal documents a user must read and accept: each is shown in full and must be scrolled to the
// end before it can be accepted.
//  - LegalReadDialog: the dialog itself (also used by the sign-up and vendor invitation pages,
//    before the account exists)
//  - LegalAcceptanceGate (default): asks the signed-in user to accept documents they have not
//    accepted yet: new ones, updated versions, and the yearly reminder. The app can't be used
//    until they accept (or sign out). Shown on the sign-in page and on every dashboard page.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack, Box, Checkbox, FormControlLabel, Link, Alert,
  Tabs, Tab, LinearProgress,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MY_PENDING_LEGAL_QUERY, ACCEPT_LEGAL_MUTATION } from '../graphql/queries';
import MarkdownView from './MarkdownView';

const SLUG = { terms: 'terms', privacy: 'privacy', vendor_terms: 'vendor-terms', refunds: 'refunds', notices: 'notices' };
const REASON = {
  new: 'Please read and accept to continue.',
  updated: 'We have updated this document. Please review the changes and accept to continue.',
  yearly: 'Our yearly reminder: please review this document again and confirm.',
};
// How close to the bottom (px) counts as the end of the document
const END_SLACK = 24;

// documents: [{ key, title, summary, body, reason? }]. onAccept(readKeys) once every document was
// read to the end and the box is ticked. onCancel: the left-hand button (cancelLabel).
export function LegalReadDialog({ documents, onAccept, onCancel, cancelLabel = 'Cancel', loading, error, intro, acceptLabel = 'I agree' }) {
  const [agreed, setAgreed] = useState(false);
  const [tab, setTab] = useState(0);
  const [read, setRead] = useState({});        // key -> true once scrolled to the end
  const [progress, setProgress] = useState({}); // key -> 0..100
  const scrollRef = useRef(null);
  const pending = documents || [];
  const current = pending[Math.min(tab, Math.max(pending.length - 1, 0))];
  const allRead = pending.length > 0 && pending.every(d => read[d.key]);
  const unread = pending.filter(d => !read[d.key]);

  const check = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !current) return;
    const max = el.scrollHeight - el.clientHeight;
    const pct = max <= END_SLACK ? 100 : Math.min(100, Math.round((el.scrollTop / max) * 100));
    setProgress(p => (p[current.key] >= pct ? p : { ...p, [current.key]: pct }));
    if (max - el.scrollTop <= END_SLACK) setRead(r => (r[current.key] ? r : { ...r, [current.key]: true }));
  }, [current]);

  // A new document starts at the top; a short one that needs no scrolling counts as read
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    const t = setTimeout(check, 50);
    return () => clearTimeout(t);
  }, [current?.key, check]);

  if (!pending.length) return null;

  const next = () => setTab(i => Math.min(i + 1, pending.length - 1));
  const currentIndex = pending.indexOf(current);

  return (
    <Dialog open maxWidth="md" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center"><GavelIcon color="primary" /><span>Please review our terms</span></Stack>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {intro || 'Read each document to the end. You can accept once every document has been read in full.'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {pending.length > 1 && (
          <Tabs value={currentIndex} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            {pending.map(d => (
              <Tab key={d.key} label={d.title} iconPosition="end"
                icon={read[d.key] ? <CheckCircleIcon color="success" fontSize="small" /> : undefined} sx={{ minHeight: 48 }} />
            ))}
          </Tabs>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
          <Typography fontWeight={700}>{current.title}</Typography>
          <Link href={`/legal/${SLUG[current.key] || current.key}`} target="_blank" rel="noopener noreferrer" variant="body2">Open in a new tab</Link>
        </Stack>
        <Typography variant="caption" color="primary.main">{REASON[current.reason] || REASON.new}</Typography>
        {current.summary && <Typography variant="body2" color="text.secondary"><b>In short:</b> {current.summary}</Typography>}
        <Box
          ref={scrollRef}
          onScroll={check}
          tabIndex={0}
          aria-label={`${current.title}, full text`}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, height: { xs: '45vh', md: '50vh' }, overflowY: 'auto' }}
        >
          <MarkdownView text={current.body} dense />
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <LinearProgress variant="determinate" value={progress[current.key] || 0} sx={{ flex: 1, height: 6, borderRadius: 3 }}
            color={read[current.key] ? 'success' : 'primary'} />
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120, textAlign: 'right' }}>
            {read[current.key] ? 'Read in full' : `${progress[current.key] || 0}% read`}
          </Typography>
        </Stack>
        {read[current.key] && currentIndex < pending.length - 1 && (
          <Button size="small" sx={{ alignSelf: 'flex-end' }} onClick={next}>Next: {pending[currentIndex + 1].title}</Button>
        )}
        <FormControlLabel
          sx={{ mt: 1, alignItems: 'flex-start' }}
          disabled={!allRead}
          control={<Checkbox checked={agreed && allRead} onChange={(e) => setAgreed(e.target.checked)} sx={{ pt: 0.25 }} />}
          label={<Typography variant="body2">I have read in full and agree to the {pending.map(d => d.title).join(', ')}.</Typography>}
        />
        {!allRead && (
          <Typography variant="caption" color="text.secondary">
            Still to read to the end: {unread.map(d => d.title).join(', ')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {onCancel && <Button onClick={onCancel}>{cancelLabel}</Button>}
        <Button variant="contained" disabled={!allRead || !agreed || loading}
          onClick={() => onAccept(pending.filter(d => read[d.key]).map(d => d.key))}>
          {loading ? 'Saving…' : acceptLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// onDone: called once nothing is left to accept (e.g. the sign-in page then opens the dashboard)
export default function LegalAcceptanceGate({ onLogout, onDone }) {
  const { data, error: loadError, refetch } = useQuery(MY_PENDING_LEGAL_QUERY, { fetchPolicy: 'network-only' });
  const [error, setError] = useState('');
  const [round, setRound] = useState(0); // a fresh dialog after each acceptance
  const [accept, { loading }] = useMutation(ACCEPT_LEGAL_MUTATION, {
    onCompleted: async () => { setError(''); setRound(r => r + 1); await refetch(); },
    onError: (e) => setError(e.message),
  });
  const pending = data?.myPendingLegalDocuments;

  // Nothing left to accept; if the check itself failed, the dashboard's own gate asks again
  useEffect(() => {
    if (onDone && ((pending && !pending.length) || loadError)) onDone();
  }, [pending, loadError, onDone]);

  if (!pending?.length) return null;
  return (
    <LegalReadDialog
      key={round}
      documents={pending}
      loading={loading}
      error={error}
      onCancel={onLogout}
      cancelLabel="Sign out"
      onAccept={(readKeys) => accept({ variables: { keys: pending.map(d => d.key), context: 'login', readInFull: readKeys } })}
    />
  );
}
